import React, { useMemo } from "react";
import { currencyFormatter } from "lib/utils/formatters";
import { useLocale } from "lib/hooks/useLocale";
import { classNames } from "lib/utils/helpers";

type sizeType = "sm" | "md" | "lg";
interface PriceProps {
  total: number | string;
  compareAt?: number | string;
  isDiscounted?: boolean;
  customCSS?: string;
  discountPercentage?: number;
  orientation?: "vertical" | "horizontal";
  isSplitCod?: boolean;
  customText?: string;
  size?: sizeType;
  customization?: {
    textColor?: string;
    freebieTextColor?: string;
  }
}

const SIZE_MAP: Record<sizeType, { actualSize: string; discountSize: string }> = {
  sm: {
    actualSize: "text-text-xs-medium",
    discountSize: "text-text-xs-semibold",
  },
  md: {
    actualSize: "text-text-xs-medium",
    discountSize: "text-text-sm-semibold",
  },
  lg: {
    actualSize: "text-text-xs-medium",
    discountSize: "text-text-lg-semibold",
  },
};

export const usePrice = (
  total: number | string,
): {
  formattedPrice: string;
} => {
  const formattedPrice = useMemo(() => {
    if (total === 0) {
      return "0";
    }
    const price = currencyFormatter(total);
    return price;
  }, [total]);

  if (typeof total === "string") return { formattedPrice: total };

  return { formattedPrice };
};

const Price: React.FC<PriceProps> = ({
  total,
  compareAt = 0,
  isDiscounted,
  customCSS,
  discountPercentage,
  orientation = "vertical",
  isSplitCod = false,
  customText,
  size = "sm",
  customization
}) => {
  const { t } = useLocale();
  const { formattedPrice } = usePrice(total);

  if (typeof total === "string") return <span className={`${SIZE_MAP[size].discountSize}`} style={{ color: customization?.textColor }}>{total}</span>;

  return (
    <span className={`flex flex-row items-end space-x-1`}>
      <span
        className={classNames(
          `flex ${isSplitCod ? "text-md" : `text-sm`}`,
          Boolean(compareAt) ? "items-end" : "",
          orientation === "vertical" ? "flex-col-reverse" : "flex-row items-center gap-1",
          customCSS ? customCSS : "",
        )}>
        {Boolean(compareAt) &&
          parseInt(compareAt?.toString() ?? "0") !== 0 &&
          parseInt(compareAt?.toString() ?? "0") >= parseInt(total?.toString() ?? "0") &&
          compareAt !== total && (
            <span
              style={{ color: customization?.textColor, opacity: 0.6 }}
              className={classNames(
                "text-gray-dark line-through",
                orientation === "vertical" ? "text-xxs" : `${SIZE_MAP[size].actualSize} leading-3`,
              )}>
              {currencyFormatter(compareAt)}
            </span>
          )}
        <span className="text-text-sm-semibold">
          {parseInt(formattedPrice ?? "0") === 0 ? (
            <span
              style={{
                color: customization?.freebieTextColor,
              }}
              className="text-yay-dark">{customText || t("automatic_freebies_free_text")}</span>
          ) : (
            <>
              {isDiscounted && <span>-&nbsp;</span>}
              <span
                style={{ color: customization?.textColor }}
              >{formattedPrice?.substring(0, formattedPrice.indexOf("."))}</span>
              <span style={{ color: customization?.textColor }}
                className={`${Number.isInteger(total) ? `${SIZE_MAP[size].discountSize}` : ""}`}>
                {formattedPrice?.substring(formattedPrice.indexOf("."), formattedPrice?.length)}
              </span>
            </>
          )}
        </span>
      </span>
      {Boolean(discountPercentage) && (
        <span className="text-sm font-medium text-yay-dark"
          style={{ color: customization?.textColor }}
        >{discountPercentage}% off</span>
      )}
    </span>
  );
};

export default Price;
